/**
 * Audit API Client
 * 
 * Provides an observable wrapper around audit fetch calls with comprehensive
 * metadata capture for debugging and instrumentation.
 */

import { setLastAuditMeta, newAuditId, AuditDebugMeta, setLastBackendFingerprint } from './probateDebugStore';

export type AuditCallMeta = {
    url: string;
    startedAt: number;
    endedAt?: number;
    durationMs?: number;
    status?: number;
    ok?: boolean;
    error?: string;
    responseBytes?: number;
    requestBytes?: number;
};

/**
 * Posts an audit request and returns both the data and call metadata.
 * 
 * @param caseJson - The case data to audit (will be sent as FormData file)
 * @param apiBase - Optional API base URL (defaults to "" for relative URLs via Vite proxy)
 * @returns Object containing data (if successful) and meta (always present)
 */
export async function postAudit(
    caseJson: any,
    apiBase: string = "",
    estateId?: string | null,
): Promise<{ data?: any; meta: AuditCallMeta }> {
    const url = `${apiBase}/api/audit`;
    const startedAt = Date.now();
    const id = newAuditId();

    // Prepare body as FormData with file blob (matching backend multer expectations)
    const jsonStr = JSON.stringify(caseJson);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const formData = new FormData();
    formData.append('policy', blob, 'case.json');
    if (estateId) {
        formData.append('estateId', estateId);
    }

    const requestBytes = jsonStr.length;

    // Log start to global debug store
    setLastAuditMeta({
        id,
        url,
        method: "POST",
        startedAt,
        stage: "request_started",
        requestBytes
    });

    const meta: AuditCallMeta = {
        url,
        startedAt,
        requestBytes
    };

    try {
        const response = await fetch(url, {
            method: "POST",
            body: formData,
            credentials: 'include',
            // Note: Don't set Content-Type - browser sets it with boundary for FormData
        });

        // Capture Backend Fingerprint Headers
        // Renamed from startedAt to avoid shadowing the outer `startedAt: number`
        // used for client-side duration measurement (lines 35, 93, 105, 154).
        const pid = response.headers.get("x-probate-backend-pid");
        const backendStartedAt = response.headers.get("x-probate-backend-started-at");
        const uptimeMs = response.headers.get("x-probate-backend-uptime-ms");
        const gitSha = response.headers.get("x-probate-backend-git-sha");
        const runId = response.headers.get("x-probate-run-id");

        if (pid) {
            setLastBackendFingerprint({
                pid,
                startedAt: backendStartedAt || null,
                uptimeMs: uptimeMs || null,
                gitSha: gitSha || null,
                runId: runId || null,
                capturedAt: Date.now()
            });
        }

        const endedAt = Date.now();
        meta.endedAt = endedAt;
        meta.durationMs = endedAt - startedAt;
        meta.status = response.status;
        meta.ok = response.ok;

        if (!response.ok) {
            const errorText = await response.text().catch(() => "");
            meta.error = `HTTP ${response.status}: ${response.statusText}${errorText ? ` - ${errorText}` : ""}`;

            setLastAuditMeta({
                id,
                url,
                method: "POST",
                startedAt,
                endedAt,
                stage: "request_failed",
                status: response.status,
                ok: false,
                error: meta.error,
                requestBytes
            });

            return { meta };
        }

        const data = await response.json();

        // [Defensive] Normalise decisions to Map if array (handles legacy/inconsistent backend)
        if (data.decisions && Array.isArray(data.decisions)) {
            const map: Record<string, any> = {};
            data.decisions.forEach((d: any) => {
                const key = d.decision_key || d.decisionKey || d.key;
                if (key) map[key] = d;
            });
            data.decisions = map;
        }

        let responseBytes = 0;
        // Approximate response size
        try {
            responseBytes = JSON.stringify(data).length;
            meta.responseBytes = responseBytes;
        } catch {
            meta.responseBytes = 0;
        }

        const summary = {
            snapshot_status: data?.snapshot?.status,
            regime: data?.derived?.regime ?? data?.trace?.regime ?? data?.snapshot?.regime ?? null,
            iht_status: data?.iht?.status ?? data?.snapshot?.iht_status,
            missing: [
                ...(data?.snapshot?.iht_info_needed ?? []),
                ...(data?.snapshot?.pending_missing ?? []),
                ...(data?.iht?.blockers ?? []),
            ].filter(Boolean),
            decisions: data?.decisions ?? {},
        };

        setLastAuditMeta({
            id,
            url,
            method: "POST",
            startedAt,
            endedAt,
            stage: "response_received",
            status: response.status,
            ok: true,
            requestBytes,
            responseBytes,
            durationMs: meta.durationMs,
            responseSummary: summary,
        });

        return { data, meta };

    } catch (err: any) {
        const endedAt = Date.now();
        meta.endedAt = endedAt;
        meta.durationMs = endedAt - startedAt;
        meta.ok = false;

        // Capture error message
        if (err instanceof TypeError && err.message.includes("fetch")) {
            meta.error = "Network error - could not reach the auditor service";
        } else {
            meta.error = String(err?.message ?? err);
        }

        setLastAuditMeta({
            id,
            url,
            method: "POST",
            startedAt,
            endedAt,
            stage: "request_failed",
            ok: false,
            error: meta.error,
            requestBytes
        });

        return { meta };
    }
}
